import * as React from 'react';
import { deepCopy } from 'deep-copy-ts';

import { DocumentTypeEnum } from "shared/enums/document-type-enum";
import { ISortConfig, SortOrder } from 'components/portal/GridComponents';
import { IConfirmProps } from 'components/ui/ConfirmationBox';
import { PagedResult } from 'shared/models/api/models';
import { RailDocument } from 'shared/models/api/models';
import { DocumentStatusEnum } from 'shared/enums/document-status-enum';
import { IRouteConfig } from 'helpers/route-config';
import { dateSort, stringSort } from 'helpers/HelperFunctions';
import { applicationState, getUserExistingRulebookMode } from "shared/store/applicationState";
import { getRulebookDocuments, getRailDocuments, getAllWritableDocuments, getMyDocuments } from 'shared/services/railDocumentsApi';
import { RulebookModeEnum, RulebookNameToEnum } from 'shared/enums/rulebook-mode-enum';

import { ReactComponent as StatusCurrent } from "assets/icons/folder.svg";
import { ReactComponent as StatusExpired } from "assets/icons/status-expired.svg";
import { ReactComponent as StatusExpiredFolder } from "assets/icons/status-expired-folder.svg";
import { ReactComponent as StatusRescinded } from "assets/icons/action-fields-outlined.svg";
import { ReactComponent as StatusDeleted } from "assets/icons/status-deleted.svg";
import { ReactComponent as StatusArchived } from "assets/icons/status-current.svg";
import { ReactComponent as RailDocsIcon } from "assets/icons/rail-docs-filled.svg";
import { ReactComponent as RecentActionsIcon } from "assets/icons/doc-recently-acknowledged-filled.svg";
import { ReactComponent as MemosIcon } from "assets/icons/rd-memo.svg";
import { ReactComponent as OperatingManualsIcon } from "assets/icons/rd-operating-manuals.svg";
import { ReactComponent as WorkInstructionsIcon } from "assets/icons/rd-wins.svg";
import { ReactComponent as OperatingInstructionsNoticesIcon } from "assets/icons/rb-operating-notices.svg";;
import { ReactComponent as RbNetworkRulesIcon } from "assets/icons/rb-network-rules.svg";
import { ReactComponent as RbNetworkProcsIcon } from "assets/icons/rb-network-procs.svg";
import { ReactComponent as RbOperatingNoticesIcon } from "assets/icons/rb-operating-notices.svg";
import { ReactComponent as RbNetworkFormsIcon } from "assets/icons/rb-network-rules-forms.svg";
import { ReactComponent as RbRailTrafficOperationsManualIcon } from "assets/icons/rb-operations-manual.svg";
import { ReactComponent as RbSignallingIcon } from "assets/icons/rb-signalling.svg";
import { ReactComponent as RbGlossary } from "assets/icons/rb-glossary.svg";
import { ReactComponent as RbRulebookFilled } from "assets/icons/rb-rulebook-filled.svg";
import { ReactComponent as RbNrGeneralRules } from "assets/icons/rb-nr-general-rules.svg";
import { ReactComponent as RbNrRailTrafficOperations } from "assets/icons/rb-nr-rail-traffic-operations.svg";
import { ReactComponent as RbNrSignallingOperations } from "assets/icons/rb-nr-signalling-operations.svg";
import { ReactComponent as RbNrWorkOnTrack } from "assets/icons/rb-nr-work-on-track.svg";
import { ReactComponent as AddNewActionIcon } from "assets/icons/admin-add-new-document.svg";
import { ReactComponent as EditActionIcon } from "assets/icons/admin-edit-document.svg";
import { ReactComponent as DeleteActionIcon } from "assets/icons/admin-delete-document.svg";
import { ReactComponent as RecindActionIcon } from "assets/icons/action-fields-outlined.svg";
import { ReactComponent as RbNetworkGuidingPrinciplesIcon } from "assets/icons/rb-guiding-principles.svg";
import { ReactComponent as ConsulationDocumentsIcon } from "assets/icons/consultation-documents.svg";
import { Assessment, Folder, Announcement as AwaitingActionIcon, Star as FavouriteIcon, Star } from '@mui/icons-material';
import { createElement } from 'react';


export const defaultDocumentIcon = Assessment;
const rulebookMode = getUserExistingRulebookMode();

const alternateRoot = "" + "";
const documentRoot = alternateRoot ?? "";

// traditional
const rulebookRootTraditional = documentRoot + (documentRoot === "/document" ? "/rulebook" : "/rulebook/traditional");
const railDocumentsRootTraditional = documentRoot + (documentRoot === "/document" ? "/rail" : "/rail-documents/traditional");
const myDocumentsRootTraditional = documentRoot + (documentRoot === "/document" ? "/user" : "/my-documents/traditional");

// cbtc
const rulebookRootCBTC = documentRoot + (documentRoot === "/document" ? "/rulebook" : "/rulebook/mbs");
const railDocumentsRootCBTC = documentRoot + (documentRoot === "/document" ? "/rail" : "/rail-documents/mbs");
const myDocumentsRootCBTC = documentRoot + (documentRoot === "/document" ? "/user" : "/my-documents/mbs");

const documentManagementRootTraditional = "/administration/document-management/traditional";
const documentManagementRootCBTC = "/administration/document-management/mbs";

// root types
// traditional
const operatingNoticesAndInstructionsRootTraditional = rulebookRootTraditional + "/operating-notices-and-instructions";
const networkRulesRootTraditional = rulebookRootTraditional + "/network-rules"
const expiredDocumentsRootTraditional = documentManagementRootTraditional + "/expired";

// cbtc
const operatingNoticesAndInstructionsRootCBTC = rulebookRootCBTC + "/operating-notices-and-instructions";
const networkRulesRootCBTC = rulebookRootCBTC + "/operational-rules"
const expiredDocumentsRootCBTC = documentManagementRootCBTC + "/expired";


export interface IPendingActionFilter {
    acknowledge?: boolean,
    clarify?: boolean,
    feedback?: boolean,

}

export interface IGridConfig {
    filterByStatuses?: string[],
    filterByTypes?: number[],
    filterBySubTypes?: number[],
    filterByPendingActions?: IPendingActionFilter,
    filterByFavourite?: boolean,
    filterByLastAcknowledgedDate?: number,
    sortBy?: ISortConfig,
    defaultPageSize?: number,
    pageSizes?: number[]
}

export type RoleBoolean = true | false | "trueBOH" | "trueNotBOH" | null | undefined;

export const isTrue = (value: RoleBoolean, userIsBOH: boolean) => {
    if (!value) return false;

    if (value === true) {
        return true;
    }

    if (value === "trueBOH") {
        return userIsBOH;
    }

    if (value === "trueNotBOH") {
        return !userIsBOH;
    }

    return false;
}

export interface IDocumentRules {
    canAdd?: RoleBoolean,
    canEdit?: RoleBoolean,
    canDelete?: RoleBoolean,
    canDeleteForever?: RoleBoolean,
    canFavourite?: RoleBoolean,
    canSelect?: RoleBoolean,
    canRescind?: RoleBoolean,
    canFilterByModule?: RoleBoolean,
    gridConfig?: IGridConfig,
    hasSubTypes?: boolean,
    hasStatus?: boolean
}

export const kebabCaseOf = (s: string) => {
    if (!s) return "";

    return s.toLowerCase().split(' ').join('-');
};

export interface IClassName {
    name: string,
    className?: string
}

export const getClassName = (x?: IClassName | null) => {
    if (!x) return;

    if (x.className) {
        return x.className
    } else {
        return kebabCaseOf(x.name);
    }
};

export interface IDocumentFilter extends IClassName {
    id: number,
    icon: any,
    url: string,
    redirectType?: IDocumentType,
    subFilters?: IDocumentFilter[],
    rules?: IDocumentRules,
    requiredPermission?: IDocumentAction,
    hidden?: RoleBoolean,
    documentListTitle?: string,
    defaultSortOrder?: SortOrder,
    defaultSortField?: string
    mode: RulebookModeEnum;
}

const favouriteFilterPasses = (config: IGridConfig, doc: RailDocument) => {
    if (config.filterByFavourite && !doc.isFavourite) {
        return false;
    }

    return true;
};

const dateDifference = (firstDate: Date, secondDate: Date) => {
    // have to convert the dates to ticks to compare them
    return firstDate.getTime() - secondDate.getTime();
};

const recentlyActionedFilterPasses = (config: IGridConfig, doc: RailDocument) => {
    if (config.filterByLastAcknowledgedDate && config.filterByLastAcknowledgedDate > 0) {
        if (doc.pendingActions?.acknowledgedDate) {
            // dates are not dates, they need to be converted to dates
            if (dateDifference(new Date(), new Date(doc.pendingActions.acknowledgedDate)) < config.filterByLastAcknowledgedDate) {
                return true;
            }
        }

        return false;
    }

    return true;
};

const statusFilterPasses = (config: IGridConfig, doc: RailDocument) => {
    //Default to only allowing current unless specifically specified another status
    if (config.filterByStatuses === undefined && doc.status !== DocumentStatusEnum.Current) {
        return false;
    }

    if (config.filterByStatuses?.indexOf(doc.statusDescription) === -1) {
        return false;
    }

    return true;
};

const subTypeFilterPasses = (config: IGridConfig, doc: RailDocument) => {
    if (config.filterBySubTypes === undefined) {
        return true;
    }

    if (config.filterBySubTypes?.some(t => t === doc.subTypeId)) {
        return true;
    }

    return false;
}

const typeFilterPasses = (config: IGridConfig, doc: RailDocument) => {
    if (config.filterByTypes === undefined) {
        return true;
    }

    if (config.filterByTypes?.some(t => t === doc.typeId)) {
        return true;
    }

    return false;
}

const pendingActionFilterPasses = (config: IGridConfig, doc: RailDocument) => {
    if (config.filterByPendingActions) {
        if (!doc.pendingActions) {
            return false;
        }

        if (config.filterByPendingActions.acknowledge) {
            if (config.filterByPendingActions.acknowledge === doc.pendingActions.acknowledge) {
                return true;
            }
        }

        if (config.filterByPendingActions.clarify) {
            if (config.filterByPendingActions.clarify === doc.pendingActions.clarify) {
                return true;
            }
        }
        if (config.filterByPendingActions.feedback) {
            if (config.filterByPendingActions.feedback === doc.pendingActions.feedback) {
                return true;
            }
        }

        return false;
    }

    return true;
};

const moduleFilterPasses = (doc: RailDocument, moduleFilter?: IDocumentModule | null) => {
    if (!moduleFilter) return true;

    if (doc.moduleId !== moduleFilter.id) {
        return false;
    }

    // If moduleId matches, further filter by the module's specific mode (Traditional or CBTC)
    if (moduleFilter.mode === RulebookModeEnum.Traditional) {
        return doc.traditional === true;
    } else if (moduleFilter.mode === RulebookModeEnum.CBTC) {
        return doc.cbtc === true;
    }
    return true;
};

const filtersPass = (
    config: IGridConfig,
    doc: RailDocument,
    moduleFilter?: IDocumentModule | null
) => {

    if (!config) return true;

    return (moduleFilter === null || moduleFilterPasses(doc, moduleFilter))
        && favouriteFilterPasses(config, doc)
        && statusFilterPasses(config, doc)
        && subTypeFilterPasses(config, doc)
        && typeFilterPasses(config, doc)
        && recentlyActionedFilterPasses(config, doc)
        && pendingActionFilterPasses(config, doc)
}

export const defaultDocumentFilter = (
    doc: RailDocument,
    view: IDocumentFilter | null,
    filter?: IDocumentFilter | null,
    subFilter?: IDocumentFilter | null,
    moduleFilter?: IDocumentModule | null,
    userFilter?: IDocumentFilter | null
) => {
    if (!doc) return false;

    var gridConfig = subFilter?.rules?.gridConfig ?? filter?.rules?.gridConfig ?? view?.rules?.gridConfig;

    if (!gridConfig) {
        return true;
    }

    if (userFilter?.rules?.gridConfig) {
        if (!filtersPass(userFilter.rules.gridConfig, doc, null))
            return false;
    }

    return filtersPass(gridConfig, doc, moduleFilter);
};

export interface IDocumentSubType extends IDocumentFilter {
    id: number
}

export interface IDocumentType extends IDocumentSubType {
    id: number,
    subTypes: IDocumentSubType[],
    preventRelatedDocumentTarget?: boolean
}

export interface IDocumentView extends IDocumentFilter {
    filters: IDocumentFilter[],
    defaultFilter?: IDocumentFilter,
    dataSource?: (params?: any) => Promise<RailDocument[]>
    pagedDataSource?: (pageSize?: number, pageNumber?: number, mode?: string) => Promise<PagedResult<RailDocument>>,
    recentWindow?: number,
    filterTitle?: string,
    cache?: IRailDocumentCache,
    sortPredicate?: (a: RailDocument, b: RailDocument) => number
}

export interface ICache<T> {
    data: T,
    lastRefresh: Date,
    refresh: () => Promise<ICache<T>>,
    maxAge: number
}

export interface IRailDocumentCache extends ICache<RailDocument[]> {
}

export interface IDocumentModule extends IDocumentView {
    id: number,
    documentTypes: IDocumentType[],
    prefix: string,
    maxDisplayed?: number
}

export const GeneralRulesDocumentSubTypeTraditional = {
    name: "General Rules",
    id: DocumentTypeEnum.GeneralRules,
    url: networkRulesRootTraditional + "/general-rules",
    icon: RbNrGeneralRules,
    defaultSortOrder: SortOrder.ASC,
    defaultSortField: "title",
    rules: {
        hasSubTypes: true,
        gridConfig: {
            filterBySubTypes: [DocumentTypeEnum.GeneralRules]
        }
    },
    mode: RulebookModeEnum.Traditional
} as IDocumentSubType;

export const GeneralRulesDocumentSubTypeCBTC = {
    name: "General Rules",
    id: DocumentTypeEnum.GeneralRules,
    url: networkRulesRootCBTC + "/general-rules",
    icon: RbNrGeneralRules,
    defaultSortOrder: SortOrder.ASC,
    defaultSortField: "title",
    rules: {
        hasSubTypes: true,
        gridConfig: {
            filterBySubTypes: [DocumentTypeEnum.GeneralRules]
        }
    },
    mode: RulebookModeEnum.CBTC
} as IDocumentSubType;

export const RailTrafficOperationsDocumentSubTypeTraditional = {
    name: "Rail Traffic Operations",
    id: DocumentTypeEnum.RailTrafficOperations,
    url: networkRulesRootTraditional + "/rail-traffic-operations",
    icon: RbNrRailTrafficOperations,
    defaultSortOrder: SortOrder.ASC,
    defaultSortField: "title",
    rules: {
        hasSubTypes: true,
        gridConfig: {
            filterBySubTypes: [DocumentTypeEnum.RailTrafficOperations]
        }
    },
    mode: RulebookModeEnum.Traditional
} as IDocumentSubType;

export const RailTrafficOperationsDocumentSubTypeCBTC = {
    name: "Rail Traffic Operations",
    id: DocumentTypeEnum.RailTrafficOperations,
    url: networkRulesRootCBTC + "/rail-traffic-operations",
    icon: RbNrRailTrafficOperations,
    defaultSortOrder: SortOrder.ASC,
    defaultSortField: "title",
    rules: {
        hasSubTypes: true,
        gridConfig: {
            filterBySubTypes: [DocumentTypeEnum.RailTrafficOperations]
        }
    },
    mode: RulebookModeEnum.CBTC
} as IDocumentSubType;

export const SignallingOperationsDocumentSubTypeTraditional = {
    name: "Signalling Operations",
    id: DocumentTypeEnum.SignallingOperations,
    hasSubTypes: true,
    url: networkRulesRootTraditional + "/signalling-operations",
    icon: RbNrSignallingOperations,
    defaultSortOrder: SortOrder.ASC,
    defaultSortField: "title",
    rules: {
        hasSubTypes: true,
        gridConfig: {
            filterBySubTypes: [DocumentTypeEnum.SignallingOperations]
        }
    },
    mode: RulebookModeEnum.Traditional
} as IDocumentSubType;

export const SignallingOperationsDocumentSubTypeCBTC = {
    name: "Signalling Operations",
    id: DocumentTypeEnum.SignallingOperations,
    hasSubTypes: true,
    url: networkRulesRootCBTC + "/signalling-operations",
    icon: RbNrSignallingOperations,
    defaultSortOrder: SortOrder.ASC,
    defaultSortField: "title",
    rules: {
        hasSubTypes: true,
        gridConfig: {
            filterBySubTypes: [DocumentTypeEnum.SignallingOperations]
        }
    },
    mode: RulebookModeEnum.CBTC
} as IDocumentSubType;

export const WorkOnTrackDocumentSubTypeTraditional = {
    name: "Work on Track",
    id: DocumentTypeEnum.WorkOnTrack,
    url: networkRulesRootTraditional + "/work-on-track",
    icon: RbNrWorkOnTrack,
    defaultSortOrder: SortOrder.ASC,
    defaultSortField: "title",
    rules: {
        hasSubTypes: true,
        gridConfig: {
            filterBySubTypes: [DocumentTypeEnum.WorkOnTrack]
        }
    },
    mode: RulebookModeEnum.Traditional
} as IDocumentSubType;

export const WorkOnTrackDocumentSubTypeCBTC = {
    name: "Work on Track",
    id: DocumentTypeEnum.WorkOnTrack,
    url: networkRulesRootCBTC + "/work-on-track",
    icon: RbNrWorkOnTrack,
    defaultSortOrder: SortOrder.ASC,
    defaultSortField: "title",
    rules: {
        hasSubTypes: true,
        gridConfig: {
            filterBySubTypes: [DocumentTypeEnum.WorkOnTrack]
        }
    },
    mode: RulebookModeEnum.CBTC
} as IDocumentSubType;

export const NonMBSTerritoryDocumentSubTypeCBTC = {
    name: "Non MBS Territory",
    id: DocumentTypeEnum.NonMBSTerritory,
    url: networkRulesRootCBTC + "/non-mbs-territory",
    icon: RbNrGeneralRules,
    defaultSortOrder: SortOrder.ASC,
    defaultSortField: "title",
    rules: {
        hasSubTypes: true,
        gridConfig: {
            filterBySubTypes: [DocumentTypeEnum.NonMBSTerritory]
        }
    },
    mode: RulebookModeEnum.CBTC
} as IDocumentSubType;

export const NetworkRulesDocumentTypes = [GeneralRulesDocumentSubTypeTraditional, RailTrafficOperationsDocumentSubTypeTraditional, SignallingOperationsDocumentSubTypeTraditional,
    WorkOnTrackDocumentSubTypeTraditional, GeneralRulesDocumentSubTypeCBTC, RailTrafficOperationsDocumentSubTypeCBTC, SignallingOperationsDocumentSubTypeCBTC,
    WorkOnTrackDocumentSubTypeCBTC, NonMBSTerritoryDocumentSubTypeCBTC];

const NetworkRulesDocumentTypesTraditional = NetworkRulesDocumentTypes.filter(t => t.mode === RulebookModeEnum.Traditional);
export const NetworkRulesDocumentTypeTraditional = {
    name: "Network Rules",
    id: DocumentTypeEnum.NetworkRules,
    url: rulebookRootTraditional + "/network-rules",
    redirectType: GeneralRulesDocumentSubTypeTraditional,
    subTypes: NetworkRulesDocumentTypesTraditional,
    subFilters: NetworkRulesDocumentTypesTraditional,
    icon: RbNetworkRulesIcon,
    defaultSortOrder: SortOrder.ASC,
    defaultSortField: "title",
    rules: {
        hasSubTypes: true,
        gridConfig: {
            filterByTypes: [DocumentTypeEnum.NetworkRules]
        }
    },
    mode: RulebookModeEnum.Traditional
} as IDocumentType;

const NetworkRulesDocumentTypesCBTC = NetworkRulesDocumentTypes.filter(t => t.mode === RulebookModeEnum.CBTC);
export const NetworkRulesDocumentTypeCBTC = {
    name: "Operational Rules",
    id: DocumentTypeEnum.NetworkRules,
    url: rulebookRootCBTC + "/operational-rules",
    redirectType: GeneralRulesDocumentSubTypeCBTC,
    subTypes: NetworkRulesDocumentTypesCBTC,
    subFilters: NetworkRulesDocumentTypesCBTC,
    icon: RbNetworkRulesIcon,
    defaultSortOrder: SortOrder.ASC,
    defaultSortField: "title",
    rules: {
        hasSubTypes: true,
        gridConfig: {
            filterByTypes: [DocumentTypeEnum.NetworkRules]
        }
    },
    mode: RulebookModeEnum.CBTC
} as IDocumentType;

export const RailNetworkGuidingPrinciplesDocumentTypeCBTC = {
    name: "Rail Network Guiding Principles",
    id: DocumentTypeEnum.RailNetworkGuidingPrinciples,
    url: rulebookRootCBTC + "/rail-network-guiding-principles",
    icon: RbNetworkGuidingPrinciplesIcon,
    defaultSortOrder: SortOrder.ASC,
    defaultSortField: "title",
    rules: {
        hasSubTypes: false,
        gridConfig: {
            filterByTypes: [DocumentTypeEnum.RailNetworkGuidingPrinciples]
        }
    },
    mode: RulebookModeEnum.CBTC
} as IDocumentType;

export const NetworkProceduresDocumentTypeTraditional = {
    name: "Network Procedures",
    id: DocumentTypeEnum.NetworkProcedures,
    url: rulebookRootTraditional + "/network-procedures",
    icon: RbNetworkProcsIcon,
    defaultSortOrder: SortOrder.ASC,
    defaultSortField: "title",
    rules: {
        hasSubTypes: false,
        gridConfig: {
            filterByTypes: [DocumentTypeEnum.NetworkProcedures]
        }
    },
    mode: RulebookModeEnum.Traditional
} as IDocumentType;

export const NetworkProceduresDocumentTypeCBTC = {
    name: "Operational Procedures",
    id: DocumentTypeEnum.NetworkProcedures,
    url: rulebookRootCBTC + "/operational-procedures",
    icon: RbNetworkProcsIcon,
    defaultSortOrder: SortOrder.ASC,
    defaultSortField: "title",
    rules: {
        hasSubTypes: false,
        gridConfig: {
            filterByTypes: [DocumentTypeEnum.NetworkProcedures]
        }
    },
    mode: RulebookModeEnum.CBTC
} as IDocumentType;

export const OperatingNoticesDocumentSubTypeTraditional = {
    name: "Operating Notices",
    id: DocumentTypeEnum.OperatingNotices,
    url: operatingNoticesAndInstructionsRootTraditional + "/operating-notices",
    icon: RbOperatingNoticesIcon,
    defaultSortOrder: SortOrder.ASC,
    defaultSortField: "title",
    rules: {
        canFilterByModule: true,
        hasSubTypes: true,
        gridConfig: {
            filterBySubTypes: [DocumentTypeEnum.OperatingNotices]
        }
    },
    mode: RulebookModeEnum.Traditional
} as IDocumentSubType;
export const OperatingNoticesDocumentSubTypeCBTC = {
    name: "Operating Notices",
    id: DocumentTypeEnum.OperatingNotices,
    url: operatingNoticesAndInstructionsRootCBTC + "/operating-notices",
    icon: RbOperatingNoticesIcon,
    defaultSortOrder: SortOrder.ASC,
    defaultSortField: "title",
    rules: {
        canFilterByModule: true,
        hasSubTypes: true,
        gridConfig: {
            filterBySubTypes: [DocumentTypeEnum.OperatingNotices]
        }
    },
    mode: RulebookModeEnum.CBTC
} as IDocumentSubType;
export const OperatingInstructionsDocumentSubTypeTraditional = {
    name: "Operating Instructions",
    id: DocumentTypeEnum.OperatingInstructions,
    url: operatingNoticesAndInstructionsRootTraditional + "/operating-instructions",
    icon: OperatingInstructionsNoticesIcon,
    defaultSortOrder: SortOrder.ASC,
    defaultSortField: "title",
    rules: {
        canFilterByModule: true,
        hasSubTypes: true,
        gridConfig: {
            filterBySubTypes: [DocumentTypeEnum.OperatingInstructions]
        }
    },
    mode: RulebookModeEnum.Traditional
} as IDocumentSubType;
export const OperatingInstructionsDocumentSubTypeCBTC = {
    name: "Operating Instructions",
    id: DocumentTypeEnum.OperatingInstructions,
    url: operatingNoticesAndInstructionsRootCBTC + "/operating-instructions",
    icon: OperatingInstructionsNoticesIcon,
    defaultSortOrder: SortOrder.ASC,
    defaultSortField: "title",
    rules: {
        canFilterByModule: true,
        hasSubTypes: true,
        gridConfig: {
            filterBySubTypes: [DocumentTypeEnum.OperatingInstructions]
        }
    },
    mode: RulebookModeEnum.CBTC
} as IDocumentSubType;
export const OperatingNoticesAndInstructionsDocumentTypes = [OperatingNoticesDocumentSubTypeTraditional, OperatingInstructionsDocumentSubTypeTraditional,
    OperatingNoticesDocumentSubTypeCBTC, OperatingInstructionsDocumentSubTypeCBTC];
const OperatingNoticesAndInstructionsDocumentTypesTraditional = OperatingNoticesAndInstructionsDocumentTypes.filter(o => o.mode === RulebookModeEnum.Traditional);
const OperatingNoticesAndInstructionsDocumentTypesCBTC = OperatingNoticesAndInstructionsDocumentTypes.filter(o => o.mode === RulebookModeEnum.CBTC);
export const OperatingNoticesAndInstructionsDocumentTypeTraditional = {
    name: "Operating Notices and Instructions",
    id: DocumentTypeEnum.OperatingNoticesAndInstructions,
    url: operatingNoticesAndInstructionsRootTraditional,
    redirectType: OperatingNoticesDocumentSubTypeTraditional,
    icon: OperatingInstructionsNoticesIcon,
    subTypes: OperatingNoticesAndInstructionsDocumentTypesTraditional,
    subFilters: OperatingNoticesAndInstructionsDocumentTypesTraditional,
    rules: {
        canFilterByModule: true,
        hasSubTypes: true,
        gridConfig: {
            filterByTypes: [DocumentTypeEnum.OperatingNoticesAndInstructions]
        }
    },
    mode: RulebookModeEnum.Traditional
} as IDocumentType;
export const OperatingNoticesAndInstructionsDocumentTypeCBTC = {
    name: "Operating Notices and Instructions",
    id: DocumentTypeEnum.OperatingNoticesAndInstructions,
    url: operatingNoticesAndInstructionsRootCBTC,
    redirectType: OperatingNoticesDocumentSubTypeCBTC,
    icon: OperatingInstructionsNoticesIcon,
    subTypes: OperatingNoticesAndInstructionsDocumentTypesCBTC,
    subFilters: OperatingNoticesAndInstructionsDocumentTypesCBTC,
    rules: {
        canFilterByModule: true,
        hasSubTypes: true,
        gridConfig: {
            filterByTypes: [DocumentTypeEnum.OperatingNoticesAndInstructions]
        }
    },
    mode: RulebookModeEnum.CBTC
} as IDocumentType;

export const NetworkFormsDocumentTypeTraditional = {
    name: "Network Forms",
    id: DocumentTypeEnum.NetworkForms,
    url: rulebookRootTraditional + "/network-forms",
    icon: RbNetworkFormsIcon,
    defaultSortOrder: SortOrder.ASC,
    defaultSortField: "title",
    rules: {
        hasSubTypes: false,
        gridConfig: {
            filterByTypes: [DocumentTypeEnum.NetworkForms]
        }
    },
    mode: RulebookModeEnum.Traditional
} as IDocumentType;

export const NetworkFormsDocumentTypeCBTC = {
    name: "Operational Forms",
    id: DocumentTypeEnum.NetworkForms,
    url: rulebookRootCBTC + "/operational-forms",
    icon: RbNetworkFormsIcon,
    defaultSortOrder: SortOrder.ASC,
    defaultSortField: "title",
    rules: {
        hasSubTypes: false,
        gridConfig: {
            filterByTypes: [DocumentTypeEnum.NetworkForms]
        }
    },
    mode: RulebookModeEnum.CBTC
} as IDocumentType;

export const RailTrafficOperationsManualDocumentTypeCBTC = {
    name: "Rail Traffic Operations Manual",
    id: DocumentTypeEnum.RailTrafficOperationsManual,
    url: rulebookRootCBTC + "/rail-traffic-operations-manual",
    icon: RbRailTrafficOperationsManualIcon,
    defaultSortOrder: SortOrder.ASC,
    defaultSortField: "title",
    rules: {
        hasSubTypes: false,
        gridConfig: {
            filterByTypes: [DocumentTypeEnum.RailTrafficOperationsManual]
        }
    },
    mode: RulebookModeEnum.CBTC
} as IDocumentType;

export const RailTrafficOperationsManualDocumentTypeTraditional = {
    name: "Rail Traffic Operations Manual",
    id: DocumentTypeEnum.RailTrafficOperationsManual,
    url: rulebookRootTraditional + "/rail-traffic-operations-manual",
    icon: RbRailTrafficOperationsManualIcon,
    defaultSortOrder: SortOrder.ASC,
    defaultSortField: "title",
    rules: {
        hasSubTypes: false,
        gridConfig: {
            filterByTypes: [DocumentTypeEnum.RailTrafficOperationsManual]
        }
    },
    mode: RulebookModeEnum.Traditional
} as IDocumentType;

export const NetworkSignalsAndSignsManualDocumentTypeTraditional = {
    name: "Network Signals and Signs Manual",
    id: DocumentTypeEnum.NetworkSignalsAndSignsManual,
    url: rulebookRootTraditional + "/network-signals-and-signs-manual",
    icon: RbSignallingIcon,
    defaultSortOrder: SortOrder.ASC,
    defaultSortField: "title",
    rules: {
        hasSubTypes: false,
        gridConfig: {
            filterByTypes: [DocumentTypeEnum.NetworkSignalsAndSignsManual]
        }
    },
    mode: RulebookModeEnum.Traditional
} as IDocumentType;

export const NetworkSignalsAndSignsManualDocumentTypeCBTC = {
    name: "Operational Signals and Signs Manual",
    id: DocumentTypeEnum.NetworkSignalsAndSignsManual,
    url: rulebookRootCBTC + "/operational-signals-and-signs-manual",
    icon: RbSignallingIcon,
    defaultSortOrder: SortOrder.ASC,
    defaultSortField: "title",
    rules: {
        hasSubTypes: false,
        gridConfig: {
            filterByTypes: [DocumentTypeEnum.NetworkSignalsAndSignsManual]
        }
    },
    mode: RulebookModeEnum.CBTC
} as IDocumentType;

export const ConsultationRulebookDocumentTypeCBTC = {
    name: "Consultation",
    id: DocumentTypeEnum.ConsultationRulebook,
    url: rulebookRootCBTC + "/consultation",
    icon: ConsulationDocumentsIcon,
    defaultSortOrder: SortOrder.ASC,
    defaultSortField: "title",
    rules: {
        hasSubTypes: false,
        gridConfig: {
            filterByTypes: [DocumentTypeEnum.ConsultationRulebook]
        }
    },
    mode: RulebookModeEnum.CBTC
} as IDocumentType;

export const ConsultationRulebookDocumentTypeTraditional = {
    name: "Consultation",
    id: DocumentTypeEnum.ConsultationRulebook,
    url: rulebookRootTraditional + "/consultation",
    icon: ConsulationDocumentsIcon,
    defaultSortOrder: SortOrder.ASC,
    defaultSortField: "title",
    rules: {
        hasSubTypes: false,
        gridConfig: {
            filterByTypes: [DocumentTypeEnum.ConsultationRulebook]
        }
    },
    mode: RulebookModeEnum.Traditional
} as IDocumentType;

export const GlossaryDocumentTypeTraditional = {
    name: "Glossary",
    id: DocumentTypeEnum.Glossary,
    url: rulebookRootTraditional + "/glossary",
    icon: RbGlossary,
    preventRelatedDocumentTarget: true,
    defaultSortOrder: SortOrder.ASC,
    defaultSortField: "title",
    rules: {
        hasSubTypes: false,
        gridConfig: {
            filterByTypes: [DocumentTypeEnum.Glossary]
        }
    },
    mode: RulebookModeEnum.Traditional
} as IDocumentType;

export const GlossaryDocumentTypeCBTC = {
    name: "Glossary",
    id: DocumentTypeEnum.Glossary,
    url: rulebookRootCBTC + "/glossary",
    icon: RbGlossary,
    preventRelatedDocumentTarget: true,
    defaultSortOrder: SortOrder.ASC,
    defaultSortField: "title",
    rules: {
        hasSubTypes: false,
        gridConfig: {
            filterByTypes: [DocumentTypeEnum.Glossary]
        }
    },
    mode: RulebookModeEnum.CBTC
} as IDocumentType;

const RailRulebookDocumentTypes = [
    NetworkRulesDocumentTypeTraditional, NetworkProceduresDocumentTypeTraditional, OperatingNoticesAndInstructionsDocumentTypeTraditional,
    NetworkFormsDocumentTypeTraditional, RailTrafficOperationsManualDocumentTypeTraditional, NetworkSignalsAndSignsManualDocumentTypeTraditional,
    ConsultationRulebookDocumentTypeTraditional, GlossaryDocumentTypeTraditional, RailNetworkGuidingPrinciplesDocumentTypeCBTC, NetworkRulesDocumentTypeCBTC,
    NetworkProceduresDocumentTypeCBTC, OperatingNoticesAndInstructionsDocumentTypeCBTC, NetworkFormsDocumentTypeCBTC, RailTrafficOperationsManualDocumentTypeCBTC,
    NetworkSignalsAndSignsManualDocumentTypeCBTC, ConsultationRulebookDocumentTypeCBTC, GlossaryDocumentTypeCBTC
];

const byTitleAlphabetical = (a: RailDocument, b: RailDocument) => stringSort(a.title, b.title);
const byPublishedDateNewestToOldest = (a: RailDocument, b: RailDocument) => (dateSort(a.publishDate, b.publishDate) * -1);

const RailRulebookDocumentTypesTraditional = RailRulebookDocumentTypes.filter(t => t.mode === RulebookModeEnum.Traditional);
const RailRulebookDocumentTypesCBTC = RailRulebookDocumentTypes.filter(t => t.mode === RulebookModeEnum.CBTC);

export const RulebookDocumentMenuTraditional = {
    name: "Rulebook Related Documents",
    documentTypes: RailRulebookDocumentTypesTraditional,
    prefix: "rb",
    dataSource: getRulebookDocuments,
    defaultSortField: "title",
    defaultSortOrder: SortOrder.ASC
} as IDocumentModule;

RulebookDocumentMenuTraditional.filters = RulebookDocumentMenuTraditional.documentTypes.map(t => t as IDocumentFilter);

export const RulebookDocumentMenuCBTC = {
    name: "Rulebook Related Documents",
    documentTypes: RailRulebookDocumentTypesCBTC,
    prefix: "rb",
    dataSource: getRulebookDocuments,
    defaultSortField: "title",
    defaultSortOrder: SortOrder.ASC
} as IDocumentModule;

RulebookDocumentMenuCBTC.filters = RulebookDocumentMenuCBTC.documentTypes.map(t => t as IDocumentFilter);

export const OperatingNoticesDocumentTypeTraditional = {
    name: "Operating Notices",
    id: DocumentTypeEnum.OperatingNotices,
    url: railDocumentsRootTraditional + "/operating-notices",
    icon: RbOperatingNoticesIcon,
    defaultSortOrder: SortOrder.DESC,
    defaultSortField: "publishDate",
    rules: {
        gridConfig: {
            filterByTypes: [DocumentTypeEnum.OperatingNotices]
        }
    },
    mode: RulebookModeEnum.Traditional
} as IDocumentType;

export const OperatingNoticesDocumentTypeCBTC = {
    name: "Operating Notices",
    id: DocumentTypeEnum.OperatingNotices,
    url: railDocumentsRootCBTC + "/operating-notices",
    icon: RbOperatingNoticesIcon,
    defaultSortOrder: SortOrder.DESC,
    defaultSortField: "publishDate",
    rules: {
        gridConfig: {
            filterByTypes: [DocumentTypeEnum.OperatingNotices]
        }
    },
    mode: RulebookModeEnum.CBTC
} as IDocumentType;

export const OperatingInstructionsDocumentTypeTraditional = {
    name: "Operating Instructions",
    id: DocumentTypeEnum.OperatingInstructions,
    url: railDocumentsRootTraditional + "/operating-instructions",
    icon: OperatingInstructionsNoticesIcon,
    defaultSortOrder: SortOrder.DESC,
    defaultSortField: "publishDate",
    rules: {
        gridConfig: {
            filterByTypes: [DocumentTypeEnum.OperatingInstructions]
        }
    },
    mode: RulebookModeEnum.Traditional
} as IDocumentType;

export const OperatingInstructionsDocumentTypeCBTC = {
    name: "Operating Instructions",
    id: DocumentTypeEnum.OperatingInstructions,
    url: railDocumentsRootCBTC + "/operating-instructions",
    icon: OperatingInstructionsNoticesIcon,
    defaultSortOrder: SortOrder.DESC,
    defaultSortField: "publishDate",
    rules: {
        gridConfig: {
            filterByTypes: [DocumentTypeEnum.OperatingInstructions]
        }
    },
    mode: RulebookModeEnum.CBTC
} as IDocumentType;

export const MemosDocumentTypeTraditional = {
    name: "Memos",
    id: DocumentTypeEnum.Memos,
    url: railDocumentsRootTraditional + "/memos",
    icon: MemosIcon,
    defaultSortOrder: SortOrder.DESC,
    defaultSortField: "publishDate",
    rules: {
        gridConfig: {
            filterByTypes: [DocumentTypeEnum.Memos]
        }
    },
    mode: RulebookModeEnum.Traditional
} as IDocumentType;

export const MemosDocumentTypeCBTC = {
    name: "Memos",
    id: DocumentTypeEnum.Memos,
    url: railDocumentsRootCBTC + "/memos",
    icon: MemosIcon,
    defaultSortOrder: SortOrder.DESC,
    defaultSortField: "publishDate",
    rules: {
        gridConfig: {
            filterByTypes: [DocumentTypeEnum.Memos]
        }
    },
    mode: RulebookModeEnum.CBTC
} as IDocumentType;

export const RailControlOperatingInstructionsDocumentTypeTraditional = {
    name: "Rail Control Operating Instructions",
    id: DocumentTypeEnum.RailControlOperatingInstructions,
    url: railDocumentsRootTraditional + "/rail-control-operating-instructions",
    icon: RailDocsIcon,
    defaultSortOrder: SortOrder.DESC,
    defaultSortField: "publishDate",
    rules: {
        gridConfig: {
            filterByTypes: [DocumentTypeEnum.RailControlOperatingInstructions]
        }
    },
    mode: RulebookModeEnum.Traditional
} as IDocumentType;

export const RailControlOperatingInstructionsDocumentTypeCBTC = {
    name: "Rail Control Operating Instructions",
    id: DocumentTypeEnum.RailControlOperatingInstructions,
    url: railDocumentsRootCBTC + "/rail-control-operating-instructions",
    icon: RailDocsIcon,
    defaultSortOrder: SortOrder.DESC,
    defaultSortField: "publishDate",
    rules: {
        gridConfig: {
            filterByTypes: [DocumentTypeEnum.RailControlOperatingInstructions]
        }
    },
    mode: RulebookModeEnum.CBTC
} as IDocumentType;

export const TrackGeneralInstructionsDocumentTypeTraditional = {
    name: "Track General Instructions",
    id: DocumentTypeEnum.TrackGeneralInstructions,
    url: railDocumentsRootTraditional + "/track-general-instructions",
    icon: RailDocsIcon,
    defaultSortOrder: SortOrder.DESC,
    defaultSortField: "publishDate",
    rules: {
        gridConfig: {
            filterByTypes: [DocumentTypeEnum.TrackGeneralInstructions]
        }
    },
    mode: RulebookModeEnum.Traditional
} as IDocumentType;

export const TrackGeneralInstructionsDocumentTypeCBTC = {
    name: "Track General Instructions",
    id: DocumentTypeEnum.TrackGeneralInstructions,
    url: railDocumentsRootCBTC + "/track-general-instructions",
    icon: RailDocsIcon,
    defaultSortOrder: SortOrder.DESC,
    defaultSortField: "publishDate",
    rules: {
        gridConfig: {
            filterByTypes: [DocumentTypeEnum.TrackGeneralInstructions]
        }
    },
    mode: RulebookModeEnum.CBTC
} as IDocumentType;

export const TrackEngineeringInstructionsDocumentTypeTraditional = {
    name: "Track Engineering Instructions",
    id: DocumentTypeEnum.TrackEngineeringInstructions,
    url: railDocumentsRootTraditional + "/track-engineering-instructions",
    icon: RailDocsIcon,
    defaultSortOrder: SortOrder.DESC,
    defaultSortField: "publishDate",
    rules: {
        gridConfig: {
            filterByTypes: [DocumentTypeEnum.TrackEngineeringInstructions]
        }
    },
    mode: RulebookModeEnum.Traditional
} as IDocumentType;

export const TrackEngineeringInstructionsDocumentTypeCBTC = {
    name: "Track Engineering Instructions",
    id: DocumentTypeEnum.TrackEngineeringInstructions,
    url: railDocumentsRootCBTC + "/track-engineering-instructions",
    icon: RailDocsIcon,
    defaultSortOrder: SortOrder.DESC,
    defaultSortField: "publishDate",
    rules: {
        gridConfig: {
            filterByTypes: [DocumentTypeEnum.TrackEngineeringInstructions]
        }
    },
    mode: RulebookModeEnum.CBTC
} as IDocumentType;

export const SignallingGeneralInstructionsDocumentTypeTraditional = {
    name: "Signalling General Instructions",
    id: DocumentTypeEnum.SignalingGeneralInstructions,
    url: railDocumentsRootTraditional + "/signaling-general-instructions",
    icon: RailDocsIcon,
    defaultSortOrder: SortOrder.DESC,
    defaultSortField: "publishDate",
    rules: {
        gridConfig: {
            filterByTypes: [DocumentTypeEnum.SignalingGeneralInstructions]
        }
    },
    mode: RulebookModeEnum.Traditional
} as IDocumentType;

export const SignallingGeneralInstructionsDocumentTypeCBTC = {
    name: "Signalling General Instructions",
    id: DocumentTypeEnum.SignalingGeneralInstructions,
    url: railDocumentsRootCBTC + "/signaling-general-instructions",
    icon: RailDocsIcon,
    defaultSortOrder: SortOrder.DESC,
    defaultSortField: "publishDate",
    rules: {
        gridConfig: {
            filterByTypes: [DocumentTypeEnum.SignalingGeneralInstructions]
        }
    },
    mode: RulebookModeEnum.CBTC
} as IDocumentType;

export const WorkInstructionsDocumentTypeTraditional = {
    name: "Work Instructions",
    id: DocumentTypeEnum.WorkInstructions,
    url: railDocumentsRootTraditional + "/work-instructions",
    icon: WorkInstructionsIcon,
    preventRelatedDocumentTarget: true,
    defaultSortOrder: SortOrder.DESC,
    defaultSortField: "publishDate",
    rules: {
        gridConfig: {
            filterByTypes: [DocumentTypeEnum.WorkInstructions]
        }
    },
    mode: RulebookModeEnum.Traditional
} as IDocumentType;

export const WorkInstructionsDocumentTypeCBTC = {
    name: "Work Instructions",
    id: DocumentTypeEnum.WorkInstructions,
    url: railDocumentsRootCBTC + "/work-instructions",
    icon: WorkInstructionsIcon,
    preventRelatedDocumentTarget: true,
    defaultSortOrder: SortOrder.DESC,
    defaultSortField: "publishDate",
    rules: {
        gridConfig: {
            filterByTypes: [DocumentTypeEnum.WorkInstructions]
        }
    },
    mode: RulebookModeEnum.CBTC
} as IDocumentType;

export const OperatingManualsDocumentTypeTraditional = {
    name: "Operating Manuals",
    id: DocumentTypeEnum.OperatingManuals,
    url: railDocumentsRootTraditional + "/operating-manuals",
    icon: OperatingManualsIcon,
    preventRelatedDocumentTarget: true,
    defaultSortOrder: SortOrder.DESC,
    defaultSortField: "publishDate",
    rules: {
        gridConfig: {
            filterByTypes: [DocumentTypeEnum.OperatingManuals]
        }
    },
    mode: RulebookModeEnum.Traditional
} as IDocumentType;

export const OperatingManualsDocumentTypeCBTC = {
    name: "Operating Manuals",
    id: DocumentTypeEnum.OperatingManuals,
    url: railDocumentsRootCBTC + "/operating-manuals",
    icon: OperatingManualsIcon,
    preventRelatedDocumentTarget: true,
    defaultSortOrder: SortOrder.DESC,
    defaultSortField: "publishDate",
    rules: {
        gridConfig: {
            filterByTypes: [DocumentTypeEnum.OperatingManuals]
        }
    },
    mode: RulebookModeEnum.CBTC
} as IDocumentType;

export const ConsultationDocumentsDocumentTypeTraditional = {
    name: "Consultation",
    id: DocumentTypeEnum.ConsultationDocuments,
    url: railDocumentsRootTraditional + "/consultation",
    icon: ConsulationDocumentsIcon,
    preventRelatedDocumentTarget: true,
    defaultSortOrder: SortOrder.DESC,
    defaultSortField: "publishDate",
    rules: {
        gridConfig: {
            filterByTypes: [DocumentTypeEnum.ConsultationDocuments]
        }
    },
    mode: RulebookModeEnum.Traditional
} as IDocumentType;

export const ConsultationDocumentsDocumentTypeCBTC = {
    name: "Consultation",
    id: DocumentTypeEnum.ConsultationDocuments,
    url: railDocumentsRootCBTC + "/consultation",
    icon: ConsulationDocumentsIcon,
    preventRelatedDocumentTarget: true,
    defaultSortOrder: SortOrder.DESC,
    defaultSortField: "publishDate",
    rules: {
        gridConfig: {
            filterByTypes: [DocumentTypeEnum.ConsultationDocuments]
        }
    },
    mode: RulebookModeEnum.CBTC
} as IDocumentType;

const RailDocumentDocumentTypes = [
    OperatingNoticesDocumentTypeTraditional, OperatingInstructionsDocumentTypeTraditional, MemosDocumentTypeTraditional,
    RailControlOperatingInstructionsDocumentTypeTraditional, TrackGeneralInstructionsDocumentTypeTraditional,
    TrackEngineeringInstructionsDocumentTypeTraditional, SignallingGeneralInstructionsDocumentTypeTraditional,
    WorkInstructionsDocumentTypeTraditional, OperatingManualsDocumentTypeTraditional, ConsultationDocumentsDocumentTypeTraditional,
    OperatingNoticesDocumentTypeCBTC, OperatingInstructionsDocumentTypeCBTC, MemosDocumentTypeCBTC,
    RailControlOperatingInstructionsDocumentTypeCBTC, TrackGeneralInstructionsDocumentTypeCBTC,
    TrackEngineeringInstructionsDocumentTypeCBTC, SignallingGeneralInstructionsDocumentTypeCBTC,
    WorkInstructionsDocumentTypeCBTC, OperatingManualsDocumentTypeCBTC, ConsultationDocumentsDocumentTypeCBTC
] as IDocumentType[];

const getRelatableDocuments = async (rulebookMode: string) => {
    var rulebookDocs = await getRulebookDocuments(rulebookMode);
    var railDocs = await getRailDocuments(rulebookMode);
    return [...rulebookDocs, ...railDocs];
}

export const RelatedRulebookDocumentViewTraditional = {
    name: "Rulebook Related Documents",
    documentTypes: [
        NetworkRulesDocumentTypeTraditional,
        NetworkProceduresDocumentTypeTraditional,
        OperatingNoticesAndInstructionsDocumentTypeTraditional,
        NetworkFormsDocumentTypeTraditional,
        RailTrafficOperationsManualDocumentTypeTraditional,
        NetworkSignalsAndSignsManualDocumentTypeTraditional,
        MemosDocumentTypeTraditional,
        RailControlOperatingInstructionsDocumentTypeTraditional,
        TrackGeneralInstructionsDocumentTypeTraditional,
        TrackEngineeringInstructionsDocumentTypeTraditional,
        SignallingGeneralInstructionsDocumentTypeTraditional
    ],
    prefix: "rb",
    dataSource: getRelatableDocuments,
    sortPredicate: (a: RailDocument, b: RailDocument) => (a.title < b.title ? -1 : a.title > b.title ? 1 : 0),
    defaultSortField: "title",
    defaultSortOrder: SortOrder.ASC
} as IDocumentModule;

export const RelatedRulebookDocumentViewCBTC = {
    name: "Rulebook Related Documents",
    documentTypes: [
        NetworkRulesDocumentTypeCBTC,
        NetworkProceduresDocumentTypeCBTC,
        OperatingNoticesAndInstructionsDocumentTypeCBTC,
        NetworkFormsDocumentTypeCBTC,
        RailTrafficOperationsManualDocumentTypeCBTC,
        NetworkSignalsAndSignsManualDocumentTypeCBTC,
        MemosDocumentTypeCBTC,
        RailControlOperatingInstructionsDocumentTypeCBTC,
        TrackGeneralInstructionsDocumentTypeCBTC,
        TrackEngineeringInstructionsDocumentTypeCBTC,
        SignallingGeneralInstructionsDocumentTypeCBTC,
        RailNetworkGuidingPrinciplesDocumentTypeCBTC
    ],
    prefix: "rb",
    dataSource: getRelatableDocuments,
    sortPredicate: (a: RailDocument, b: RailDocument) => (a.title < b.title ? -1 : a.title > b.title ? 1 : 0),
    defaultSortField: "title",
    defaultSortOrder: SortOrder.ASC
} as IDocumentModule;

RelatedRulebookDocumentViewTraditional.filters = RelatedRulebookDocumentViewTraditional.documentTypes.map(t => t as IDocumentFilter);
RelatedRulebookDocumentViewCBTC.filters = RelatedRulebookDocumentViewCBTC.documentTypes.map(t => t as IDocumentFilter);

export const initRelatedDocs = (mode?: string) => {
    const excludeFromExpandableMenus = [DocumentTypeEnum.OperatingNoticesAndInstructions, DocumentTypeEnum.NetworkSignalsAndSignsManual,
    DocumentTypeEnum.Glossary, DocumentTypeEnum.OperatingInstructions, DocumentTypeEnum.OperatingNotices]

    return new Promise<any>((resolve, reject) => {
        if (RulebookNameToEnum(mode!) === RulebookModeEnum.Traditional && RelatedRulebookDocumentViewTraditional.dataSource) {
            RelatedRulebookDocumentViewTraditional.dataSource(mode)
                .then(result => {
                    if (result) {
                        RelatedRulebookDocumentViewTraditional.cache = {
                            lastRefresh: new Date(),
                            data: result.filter(doc => RelatedRulebookDocumentViewTraditional.documentTypes.find(dt => dt.id === doc.typeId)),
                            refresh: initRelatedDocs,
                            maxAge: millisecondsPerMinute * 10 // refresh every 10 mins, if needed
                        }

                        RulebookDocumentMenuTraditional.cache = {
                            lastRefresh: new Date(),
                            data: result.filter(doc => RulebookDocumentMenuTraditional.documentTypes.find(dt => dt.id === doc.typeId)),
                            refresh: initRelatedDocs,
                            maxAge: millisecondsPerMinute * 10 // refresh every 10 mins, if needed
                        }

                        RailRulebookDocumentModuleTraditional.cache = {
                            lastRefresh: new Date(),
                            data: result.filter(doc => RailRulebookDocumentModuleTraditional.documentTypes
                                .filter(f => !excludeFromExpandableMenus.includes(f.id))
                                .find(dt => dt.id === doc.typeId)),
                            refresh: initRelatedDocs,
                            maxAge: millisecondsPerMinute * 10 // refresh every 10 mins, if needed
                        }
                    }

                    return resolve(() => {
                        RelatedRulebookDocumentViewTraditional.cache as IRailDocumentCache;
                        RailRulebookDocumentModuleTraditional.cache as IRailDocumentCache;
                    });
                })
                .catch(reject);
        } else if (RulebookNameToEnum(mode!) === RulebookModeEnum.CBTC && RelatedRulebookDocumentViewCBTC.dataSource) {
            RelatedRulebookDocumentViewCBTC.dataSource(mode)
                .then(result => {
                    if (result) {
                        RelatedRulebookDocumentViewCBTC.cache = {
                            lastRefresh: new Date(),
                            data: result.filter(doc => RelatedRulebookDocumentViewCBTC.documentTypes.find(dt => dt.id === doc.typeId)),
                            refresh: initRelatedDocs,
                            maxAge: millisecondsPerMinute * 10 // refresh every 10 mins, if needed
                        }

                        RulebookDocumentMenuCBTC.cache = {
                            lastRefresh: new Date(),
                            data: result.filter(doc => RulebookDocumentMenuCBTC.documentTypes.find(dt => dt.id === doc.typeId)),
                            refresh: initRelatedDocs,
                            maxAge: millisecondsPerMinute * 10 // refresh every 10 mins, if needed
                        }

                        RailRulebookDocumentModuleCBTC.cache = {
                            lastRefresh: new Date(),
                            data: result.filter(doc => RailRulebookDocumentModuleCBTC.documentTypes
                                .filter(f => !excludeFromExpandableMenus.includes(f.id))
                                .find(dt => dt.id === doc.typeId)),
                            refresh: initRelatedDocs,
                            maxAge: millisecondsPerMinute * 10 // refresh every 10 mins, if needed
                        }
                    }

                    return resolve(() => {
                        RelatedRulebookDocumentViewCBTC.cache as IRailDocumentCache;
                        RailRulebookDocumentModuleCBTC.cache as IRailDocumentCache;
                    });
                })
                .catch(reject);
        } else {
            return reject("No dataSource configured for cached view");
        }
    });
};

export const AwaitingActionFilterCBTC = {
    name: "Awaiting action",
    url: myDocumentsRootCBTC + "/awaiting-action",
    icon: AwaitingActionIcon,
    className: "awaiting-action",
    defaultSortOrder: SortOrder.DESC,
    defaultSortField: "pendingActions.notifyDate",
    rules: {
        hasSubTypes: true,
        hasStatus: true,
        gridConfig: {
            filterByPendingActions: {
                acknowledge: true
            }
        }
    },
    mode: RulebookModeEnum.CBTC
} as IDocumentFilter;

export const AwaitingActionFilterTraditional = {
    name: "Awaiting action",
    url: myDocumentsRootTraditional + "/awaiting-action",
    icon: AwaitingActionIcon,
    className: "awaiting-action",
    defaultSortOrder: SortOrder.DESC,
    defaultSortField: "pendingActions.notifyDate",
    rules: {
        hasSubTypes: true,
        hasStatus: true,
        gridConfig: {
            filterByPendingActions: {
                acknowledge: true
            }
        }
    },
    mode: RulebookModeEnum.Traditional
} as IDocumentFilter;

const millisecondsPerMinute = 60000;
const millisecondsPerDay = 86400000;

export const RecentlyActionedFilterTraditional = {
    name: "Recently actioned",
    url: myDocumentsRootTraditional + "/recently-actioned",
    icon: RecentActionsIcon,
    defaultSortOrder: SortOrder.DESC,
    defaultSortField: "pendingActions.acknowledgedDate",
    rules: {
        hasSubTypes: true,
        hasStatus: true,
        gridConfig: {
            filterByLastAcknowledgedDate: 30 * millisecondsPerDay
        }
    },
    mode: RulebookModeEnum.Traditional
} as IDocumentFilter;

export const RecentlyActionedFilterCBTC = {
    name: "Recently actioned",
    url: myDocumentsRootCBTC + "/recently-actioned",
    icon: RecentActionsIcon,
    defaultSortOrder: SortOrder.DESC,
    defaultSortField: "pendingActions.acknowledgedDate",
    rules: {
        hasSubTypes: true,
        hasStatus: true,
        gridConfig: {
            filterByLastAcknowledgedDate: 30 * millisecondsPerDay
        }
    },
    mode: RulebookModeEnum.CBTC
} as IDocumentFilter;

export const RecentlyActionedPath = [RecentlyActionedFilterTraditional, RecentlyActionedFilterCBTC];


export const FavouritesFilterTraditional = {
    name: "Favourites",
    url: myDocumentsRootTraditional + "/favourites",
    icon: FavouriteIcon,
    defaultSortField: "title",
    defaultSortOrder: SortOrder.ASC,
    rules: {
        hasStatus: true,
        hasSubTypes: true,
        gridConfig: {
            filterByFavourite: true
        }
    },
    mode: RulebookModeEnum.Traditional
} as IDocumentFilter;

export const FavouritesFilterCBTC = {
    name: "Favourites",
    url: myDocumentsRootCBTC + "/favourites",
    icon: FavouriteIcon,
    defaultSortField: "title",
    defaultSortOrder: SortOrder.ASC,
    rules: {
        hasStatus: true,
        hasSubTypes: true,
        gridConfig: {
            filterByFavourite: true
        }
    },
    mode: RulebookModeEnum.CBTC
} as IDocumentFilter;

export const FavouritesPath = [FavouritesFilterTraditional, FavouritesFilterCBTC];

export const CurrentFilterTraditional = {
    name: "Current",
    url: documentManagementRootTraditional + "/current",
    icon: Folder,
    defaultSortOrder: SortOrder.DESC,
    defaultSortField: "publishDate",
    rules: {
        hasSubTypes: true,
        hasStatus: true,
        canFilterByModule: true,
        gridConfig: {
            filterByStatuses: ["Current"]
        }
    },
    mode: RulebookModeEnum.Traditional
} as IDocumentFilter;

export const CurrentFilterCBTC = {
    name: "Current",
    url: documentManagementRootCBTC + "/current",
    icon: Folder,
    defaultSortOrder: SortOrder.DESC,
    defaultSortField: "publishDate",
    rules: {
        hasSubTypes: true,
        hasStatus: true,
        canFilterByModule: true,
        gridConfig: {
            filterByStatuses: ["Current"]
        }
    },
    mode: RulebookModeEnum.CBTC
} as IDocumentFilter;

export const RecindedDocumentsFilterCBTC = {
    name: "Rescinded documents",
    url: expiredDocumentsRootCBTC + "/rescinded-documents",
    icon: StatusRescinded,
    defaultSortOrder: SortOrder.DESC,
    defaultSortField: "rescindedDate",
    rules: {
        hasSubTypes: true,
        hasStatus: true,
        canFilterByModule: true,
        gridConfig: {
            filterByStatuses: ["Rescinded"]
        }
    },
    mode: RulebookModeEnum.CBTC
} as IDocumentFilter;

export const RecindedDocumentsFilterTraditional = {
    name: "Rescinded documents",
    url: expiredDocumentsRootTraditional + "/rescinded-documents",
    icon: StatusRescinded,
    defaultSortOrder: SortOrder.DESC,
    defaultSortField: "rescindedDate",
    rules: {
        hasSubTypes: true,
        hasStatus: true,
        canFilterByModule: true,
        gridConfig: {
            filterByStatuses: ["Rescinded"]
        }
    },
    mode: RulebookModeEnum.Traditional
} as IDocumentFilter;

export const ExpiredDocumentsFilterTraditional = {
    name: "Expired documents",
    url: expiredDocumentsRootTraditional + "/expired-documents",
    icon: StatusExpired,
    defaultSortOrder: SortOrder.DESC,
    defaultSortField: "expiryDate",
    rules: {
        hasSubTypes: true,
        hasStatus: true,
        canFilterByModule: true,
        gridConfig: {
            filterByStatuses: ["Expired"]
        }
    },
    mode: RulebookModeEnum.Traditional
} as IDocumentFilter;

export const ExpiredDocumentsFilterCBTC = {
    name: "Expired documents",
    url: expiredDocumentsRootCBTC + "/expired-documents",
    icon: StatusExpired,
    defaultSortOrder: SortOrder.DESC,
    defaultSortField: "expiryDate",
    rules: {
        hasSubTypes: true,
        hasStatus: true,
        canFilterByModule: true,
        gridConfig: {
            filterByStatuses: ["Expired"]
        }
    },
    mode: RulebookModeEnum.CBTC
} as IDocumentFilter;

export const ExpiredFilterTraditional = {
    name: "Expired",
    url: expiredDocumentsRootTraditional,
    icon: StatusExpiredFolder,
    subFilters: [RecindedDocumentsFilterTraditional, ExpiredDocumentsFilterTraditional],
    defaultSortOrder: SortOrder.DESC,
    defaultSortField: "expiryDate",
    rules: {
        hasSubTypes: true,
        hasStatus: true,
        canFilterByModule: true,
        gridConfig: {
            filterByStatuses: ["Expired", "Rescinded"]
        }
    },
    mode: RulebookModeEnum.Traditional
} as IDocumentFilter;

export const ExpiredFilterCBTC = {
    name: "Expired",
    url: expiredDocumentsRootCBTC,
    icon: StatusExpiredFolder,
    subFilters: [RecindedDocumentsFilterCBTC, ExpiredDocumentsFilterCBTC],
    defaultSortOrder: SortOrder.DESC,
    defaultSortField: "expiryDate",
    rules: {
        hasSubTypes: true,
        hasStatus: true,
        canFilterByModule: true,
        gridConfig: {
            filterByStatuses: ["Expired", "Rescinded"]
        }
    },
    mode: RulebookModeEnum.CBTC
} as IDocumentFilter;

export const DeletedFilterTraditional = {
    name: "Deleted",
    url: documentManagementRootTraditional + "/deleted",
    icon: StatusDeleted,
    defaultSortOrder: SortOrder.DESC,
    defaultSortField: "deletedDate",
    rules: {
        hasSubTypes: true,
        hasStatus: true,

        canFilterByModule: true,
        gridConfig: {
            filterByStatuses: ["Deleted"]
        }
    },
    mode: RulebookModeEnum.Traditional
} as IDocumentFilter;

export const DeletedFilterCBTC = {
    name: "Deleted",
    url: documentManagementRootCBTC + "/deleted",
    icon: StatusDeleted,
    defaultSortOrder: SortOrder.DESC,
    defaultSortField: "deletedDate",
    rules: {
        hasSubTypes: true,
        hasStatus: true,

        canFilterByModule: true,
        gridConfig: {
            filterByStatuses: ["Deleted"]
        }
    },
    mode: RulebookModeEnum.CBTC
} as IDocumentFilter;

const doesModuleMatch = (m: IDocumentModule, id?: number, name?: string) => (!id || m.id === id) && (!name || m.name === name);
const doesDocumentTypeMatch = (dt: IDocumentSubType, id?: number, name?: string) => (!id || dt.id === id) && (!name || dt.name === name);

export const findDocumentType = (name?: string, id?: number, moduleName?: string, moduleId?: number) => {
    const updatedRulebookMode = getUserExistingRulebookMode();
    const currentDocumentModules = DocumentModules.filter(m => m.mode === RulebookNameToEnum(updatedRulebookMode));

    for (var m of currentDocumentModules) {
        if (m.documentTypes && doesModuleMatch(m, moduleId, moduleName)) {
            for (var dt of m.documentTypes) {
                if (doesDocumentTypeMatch(dt, id, name)) {
                    return { module: m, type: dt };
                }

                if (dt.subTypes) {
                    for (var st of dt.subTypes) {
                        if (doesDocumentTypeMatch(st, id, name)) {
                            return { module: m, type: dt, subType: st };
                        }
                    }
                }
            }
        }
    }

    return null;
};

export const findDocumentViewForId = (typeId: number, subTypeId: number | null) => {
    const updatedRulebookMode = getUserExistingRulebookMode();
    const currentDocumentModules = DocumentModules.filter(m => m.mode === RulebookNameToEnum(updatedRulebookMode));

    if (currentDocumentModules) {
        for (var m of currentDocumentModules) {
            for (var type of m.documentTypes) {
                if (subTypeId && type.subTypes) {
                    for (var subType of type.subTypes) {
                        if (subType.id === subTypeId) {
                            return { module: m, type: type, subType: subType };
                        }
                    }
                } else {
                    if (type.id === typeId) {
                        return { module: m, type: type, subType: null };
                    }
                }
            }
        }
    }
};

export const findDocumentView = (url: string) => {;
    let views = DocumentViews();
    if (views) {
        for (var v of views) {
            if (v.url === url) {
                return { view: v };
            }

            if (v.filters) {
                for (var f of v.filters) {
                    if (f.url === url) {
                        return { view: v, filter: f };
                    }

                    if (f.subFilters) {
                        for (var f2 of f.subFilters) {
                            if (f2.url === url) {
                                return { view: v, filter: f, subFilter: f2 };
                            }
                        }
                    }
                }
            }
        }
    }
};

const BackOfHouseRoles = ["BackOffice", "Supervisor", "SafeWorking"];

export interface IDocumentAction {
    name: string,
    roles?: string[],
    icon?: any,
    className?: string,
    confirm: IConfirmProps
}

export const AddDocumentAction = {
    name: "Add",
    roles: BackOfHouseRoles,
    icon: AddNewActionIcon,
    className: "action-add-new-document"
} as IDocumentAction;

export const EditDocumentAction = {
    name: "Edit",
    roles: BackOfHouseRoles,
    icon: EditActionIcon,
    className: "action-edit-document"
} as IDocumentAction;

export const RescindDocumentAction = {
    name: "Rescind",
    roles: BackOfHouseRoles,
    icon: RecindActionIcon,
    className: "action-rescind-document"
} as IDocumentAction;

export const DeleteDocumentAction = {
    name: "Delete",
    roles: BackOfHouseRoles,
    icon: DeleteActionIcon,
    className: "action-delete-document",
    confirm: {
        message: "Are you sure you want to delete this document?",
        options: ["Cancel", "Delete"],
        defaultOption: "Delete"
    }
} as IDocumentAction;

export const DeletePermanentlyDocumentAction = {
    name: "Delete permanently",
    roles: BackOfHouseRoles,
    icon: DeleteActionIcon,
    className: "action-delete-document-permanently",
    confirm: {
        message: "Are you sure you want to delete these documents permanently? This action is not reversible.",
        options: ["Yes, delete", "Keep files"],
        defaultOption: "Yes, delete"
    }
} as IDocumentAction;

export const FavouriteDocumentAction = {
    name: "Favourite",
    className: "action-favourite"
} as IDocumentAction;

export const SelectDocumentAction = {
    name: "Select"
} as IDocumentAction;

export interface IDocumentStatus {
    id: number,
    name: string,
    icon: any,
    availableInMyDocuments?: boolean
    actions: IDocumentAction[]
}

export const CurrentDocumentStatus = {
    id: DocumentStatusEnum.Current,
    name: "Current",
    icon: StatusCurrent,
    availableInMyDocuments: true,
    actions: [SelectDocumentAction, FavouriteDocumentAction, AddDocumentAction, EditDocumentAction, RescindDocumentAction]
} as IDocumentStatus;

export const ExpiredDocumentStatus = {
    id: DocumentStatusEnum.Expired,
    name: "Expired",
    icon: StatusExpired,
    availableInMyDocuments: true,
    actions: [SelectDocumentAction, FavouriteDocumentAction, DeleteDocumentAction]
} as IDocumentStatus;

export const RescindedDocumentStatus = {
    id: DocumentStatusEnum.Rescinded,
    name: "Rescinded",
    icon: StatusExpired,
    availableInMyDocuments: true,
    actions: [SelectDocumentAction, FavouriteDocumentAction, DeleteDocumentAction]
} as IDocumentStatus;

export const ArchivedDocumentStatus = {
    id: DocumentStatusEnum.Archived,
    name: "Archived",
    icon: StatusArchived,
    actions: [SelectDocumentAction]
} as IDocumentStatus;

export const DeletedDocumentStatus = {
    id: DocumentStatusEnum.Deleted,
    name: "Deleted",
    icon: StatusDeleted,
    actions: [SelectDocumentAction, FavouriteDocumentAction, DeletePermanentlyDocumentAction]
} as IDocumentStatus;

export const DeletedFullDocumentStatus = {
    id: DocumentStatusEnum.DeletedFull,
    name: "DeletedFull",
    icon: StatusDeleted,
    actions: []
} as IDocumentStatus;

export const DocumentStatuses = [
    CurrentDocumentStatus, ExpiredDocumentStatus, ArchivedDocumentStatus,
    DeletedDocumentStatus, DeletedFullDocumentStatus, RescindedDocumentStatus
] as IDocumentStatus[];

const userHasRole = (requiredRoles?: string[]) => {
    if (!requiredRoles) return true;

    const userRoles = applicationState.getUser()?.roles ?? [];

    let hasRole = userRoles.some(userRole => requiredRoles.indexOf(userRole) > -1);

    return hasRole;
};

const statusHasAction = (status: IDocumentStatus, action: IDocumentAction): RoleBoolean => {
    return status.actions.includes(action);
};

const anyStatusHasAction = (action: IDocumentAction): RoleBoolean => {
    let hasAction = DocumentStatuses.some(s => statusHasAction(s, action) && userHasRole(action.roles));

    return hasAction;
};

const statusFilterHasAction = (filterByStatus: string[], action: IDocumentAction): RoleBoolean => {
    let hasAction = DocumentStatuses.some(s => filterByStatus.includes(s.name) && statusHasAction(s, action) && userHasRole(action.roles));

    return hasAction;
};

const userHasPermission = (action: IDocumentAction): boolean => {
    let hasRole = userHasRole(action.roles);

    return hasRole;
};

const autoAssignActionRules = (f: IDocumentFilter) => {
    if (f.rules) {
        const filterByStatus = f.rules.gridConfig?.filterByStatuses ?? [];
        const defaultFilterStatus = ["Current"];

        // can favourite unless otherwise set above
        f.rules.canFavourite = f.rules.canFavourite === false ? false : true;

        if (filterByStatus.length === 0) {
            f.rules.canAdd = f.rules.canAdd === true && userHasPermission(AddDocumentAction);
            f.rules.canEdit = f.rules.canEdit === false ? false : statusFilterHasAction(defaultFilterStatus, EditDocumentAction);
            f.rules.canRescind = f.rules.canRescind === false ? false : statusFilterHasAction(defaultFilterStatus, RescindDocumentAction);
            f.rules.canDelete = f.rules.canDelete === true && userHasPermission(DeleteDocumentAction);
            f.rules.canDeleteForever = f.rules.canDeleteForever === true && userHasPermission(DeletePermanentlyDocumentAction);
            f.rules.canSelect = f.rules.canSelect === false ? false : anyStatusHasAction(SelectDocumentAction);
        } else {
            f.rules.canAdd = f.rules.canAdd === false ? false : statusFilterHasAction(filterByStatus, AddDocumentAction);
            f.rules.canEdit = f.rules.canEdit === false ? false : statusFilterHasAction(filterByStatus, EditDocumentAction);
            f.rules.canRescind = f.rules.canRescind === false ? false : statusFilterHasAction(filterByStatus, RescindDocumentAction);
            f.rules.canDelete = f.rules.canDelete === false ? false : statusFilterHasAction(filterByStatus, DeleteDocumentAction);
            f.rules.canDeleteForever = f.rules.canDeleteForever === false ? false : statusFilterHasAction(filterByStatus, DeletePermanentlyDocumentAction);
            f.rules.canSelect = f.rules.canSelect === false ? false : statusFilterHasAction(filterByStatus, SelectDocumentAction);
        }
    }
};

const forEachFilter = (views: IDocumentView[], fn: (v: IDocumentFilter) => void) => {
    views.forEach(v => {
        fn(v);

        if (v.filters) {
            v.filters.forEach(f => {
                fn(f);

                if (f.subFilters) {
                    f.subFilters.forEach(fn);
                }
            })
        }
    });
}

export const MyDocumentsDocumentViewTraditional = {
    name: "My Documents",
    url: myDocumentsRootTraditional,
    icon: Folder,
    filters: [AwaitingActionFilterTraditional, RecentlyActionedFilterTraditional, FavouritesFilterTraditional],
    defaultFilter: AwaitingActionFilterTraditional,
    pagedDataSource: getMyDocuments,
    recentWindow: 30,
    defaultSortOrder: SortOrder.DESC,
    defaultSortField: "pendingActions.notifyDate",
    rules: {
        hasStatus: true,
        hasSubTypes: true,
        canFilterByModule: true
    },
    mode: RulebookModeEnum.Traditional
} as IDocumentView;

export const MyDocumentsDocumentViewCBTC = {
    name: "My Documents",
    url: myDocumentsRootCBTC,
    icon: Folder,
    filters: [AwaitingActionFilterCBTC, RecentlyActionedFilterCBTC, FavouritesFilterCBTC],
    defaultFilter: AwaitingActionFilterCBTC,
    pagedDataSource: getMyDocuments,
    recentWindow: 30,
    defaultSortOrder: SortOrder.DESC,
    defaultSortField: "pendingActions.notifyDate",
    rules: {
        hasStatus: true,
        hasSubTypes: true,
        canFilterByModule: true
    },
    mode: RulebookModeEnum.CBTC
} as IDocumentView;

export const RailRulebookDocumentModuleTraditional = {
    id: 1,
    name: "Rulebook",
    url: rulebookRootTraditional,
    icon: RbRulebookFilled,
    defaultFilter: NetworkRulesDocumentTypeTraditional,
    documentTypes: RailRulebookDocumentTypesTraditional,
    filters: RailRulebookDocumentTypesTraditional.map(t => t as IDocumentFilter),
    filterTitle: "Contents",
    prefix: "rb",
    dataSource: getRulebookDocuments,
    defaultSortOrder: SortOrder.ASC,
    defaultSortField: "title",
    sortPredicate: byTitleAlphabetical,
    rules: {
        hasSubTypes: true,
        gridConfig: {
            defaultPageSize: -1
        }
    },
    mode: RulebookModeEnum.Traditional,
    className: "traditional",
    maxDisplayed: 2,
} as IDocumentModule;

export const RailRulebookDocumentModuleCBTC = {
    id: 1,
    name: "MBS Rulebook",
    url: rulebookRootCBTC,
    icon: RbRulebookFilled,
    defaultFilter: RailNetworkGuidingPrinciplesDocumentTypeCBTC,
    documentTypes: RailRulebookDocumentTypesCBTC,
    filters: RailRulebookDocumentTypesCBTC.map(t => t as IDocumentFilter),
    filterTitle: "Contents",
    prefix: "rb",
    dataSource: getRulebookDocuments,
    defaultSortOrder: SortOrder.ASC,
    defaultSortField: "title",
    sortPredicate: byTitleAlphabetical,
    rules: {
        hasSubTypes: true,
        gridConfig: {
            defaultPageSize: -1
        }
    },
    mode: RulebookModeEnum.CBTC,
    className: "mbs",
    maxDisplayed: 2,
} as IDocumentModule;

const RailDocumentDocumentTypesTraditional = RailDocumentDocumentTypes.filter(t => t.mode === RulebookModeEnum.Traditional);
const RailDocumentDocumentTypesCBTC = RailDocumentDocumentTypes.filter(t => t.mode === RulebookModeEnum.CBTC);

export const RailDocumentDocumentModuleTraditional = {
    id: 2,
    name: "Rail Documents",
    url: railDocumentsRootTraditional,
    icon: RailDocsIcon,
    documentTypes: RailDocumentDocumentTypesTraditional,
    filters: RailDocumentDocumentTypesTraditional.map(t => t as IDocumentFilter),
    prefix: "rd",
    dataSource: getRailDocuments,
    defaultSortOrder: SortOrder.DESC,
    defaultSortField: "publishDate",
    hidden: "trueNotBOH",
    maxDisplayed: 7,
    rules: {},
    mode: RulebookModeEnum.Traditional,
    className: "rail-documents-traditional",
} as IDocumentModule;

export const RailDocumentDocumentModuleCBTC = {
    id: 2,
    name: "Rail MBS Documents",
    url: railDocumentsRootCBTC,
    icon: RailDocsIcon,
    documentTypes: RailDocumentDocumentTypesCBTC,
    filters: RailDocumentDocumentTypesCBTC.map(t => t as IDocumentFilter),
    prefix: "rd",
    dataSource: getRailDocuments,
    defaultSortOrder: SortOrder.DESC,
    defaultSortField: "publishDate",
    hidden: "trueNotBOH",
    maxDisplayed: 7,
    rules: {},
    mode: RulebookModeEnum.CBTC,
    className: "rail-documents-cbtc",
} as IDocumentModule;

export const DocumentManagementDocumentViewTraditional = {
    name: "Document Management",
    url: documentManagementRootTraditional,
    icon: Folder,
    filters: [CurrentFilterTraditional, ExpiredFilterTraditional, DeletedFilterTraditional],
    requiredPermission: EditDocumentAction,
    defaultFilter: CurrentFilterTraditional,
    dataSource: getAllWritableDocuments,
    sortPredicate: byPublishedDateNewestToOldest,
    defaultSortOrder: SortOrder.DESC,
    defaultSortField: "publishDate",
    rules: {
        hasSubTypes: true,
        hasStatus: true,
        canFilterByModule: true
    },
    mode: RulebookModeEnum.Traditional
} as IDocumentView;

export const DocumentManagementDocumentViewCBTC = {
    name: "Document Management",
    url: documentManagementRootCBTC,
    icon: Folder,
    filters: [CurrentFilterCBTC, ExpiredFilterCBTC, DeletedFilterCBTC],
    requiredPermission: EditDocumentAction,
    defaultFilter: CurrentFilterCBTC,
    dataSource: getAllWritableDocuments,
    sortPredicate: byPublishedDateNewestToOldest,
    defaultSortOrder: SortOrder.DESC,
    defaultSortField: "publishDate",
    rules: {
        hasSubTypes: true,
        hasStatus: true,
        canFilterByModule: true
    },
    mode: RulebookModeEnum.CBTC
} as IDocumentView;

let _documentViewsCache = undefined as undefined | IDocumentView[];

const getDocumentViews = () => {
    let cacheViews = false;
    if (applicationState.getUser() != null) {
        cacheViews = true;
    }

    if (cacheViews && _documentViewsCache !== undefined) {
        return _documentViewsCache;
    }

    const viewsArray = [MyDocumentsDocumentViewTraditional, RailRulebookDocumentModuleTraditional, RailDocumentDocumentModuleTraditional,DocumentManagementDocumentViewTraditional,
        MyDocumentsDocumentViewCBTC, RailRulebookDocumentModuleCBTC, RailDocumentDocumentModuleCBTC, DocumentManagementDocumentViewCBTC] as IDocumentView[];
    let views = [] as IDocumentView[];
    viewsArray.forEach(view => {
        if (cacheViews == false || view.requiredPermission == undefined || userHasPermission(view.requiredPermission)) {
            views.push(deepCopy(view));
        }
    });

    forEachFilter(views, autoAssignActionRules);

    if (cacheViews) {
        _documentViewsCache = views;
    }

    return views;
}
export const DocumentViews = () => getDocumentViews();

export const DefaultView = RulebookNameToEnum(rulebookMode) === RulebookModeEnum.Traditional ? MyDocumentsDocumentViewTraditional :
    RulebookNameToEnum(rulebookMode) === RulebookModeEnum.CBTC ? MyDocumentsDocumentViewCBTC : MyDocumentsDocumentViewTraditional;

const getDocumentModules = () => {
    return [RailRulebookDocumentModuleTraditional, RailDocumentDocumentModuleTraditional,
        RailRulebookDocumentModuleCBTC, RailDocumentDocumentModuleCBTC] as IDocumentModule[];
}

export const DocumentModules = getDocumentModules();

export const DocumentStatusIcon = (props: { status: string }) => {
    const getIconForStatus = (status: string) => {
        return DocumentStatuses.find(s => s.name == status)?.icon ?? StatusCurrent;
    }

    const Icon = getIconForStatus(status);

    return (<Icon />);
};

export const DocumentTypeIcon = (props: { type: string, subType?: string }) => {
    const docType = findDocumentType(props.subType ?? props.type);

    const Icon = !docType ? defaultDocumentIcon : (docType.subType ?? docType.type).icon ?? defaultDocumentIcon;

    return (<Icon />);
}

export const FilterIcon = (props: { filter: IDocumentFilter }) => {
    const Icon = props.filter?.icon ?? Folder;

    return (<Icon />);
}

export const AllDocumentRoutes = (component: any) => {
    const routes = [] as any[];
    let views = DocumentViews();
    if (views) {
        for (var v of views) {
            if (v.url) {
                if (v.url.includes("mbs") || v.url.includes("cbtc")) {
                    routes.push({
                        path: v.url, element: createElement(component), breadCrumb: v.name, permission: "web.cbtc.user.edit"
                    } as IRouteConfig);
                    routes.push({
                        path: v.url + "/:typeFilter", element: createElement(component), breadCrumb: undefined, permission: "web.cbtc.user.edit"
                    } as IRouteConfig);
                    routes.push({
                        path: v.url + "/expired" + "/:typeFilter", element: createElement(component), breadCrumb: undefined, permission: "web.cbtc.user.edit"
                    } as IRouteConfig);

                    routes.push({
                        path: v.url + "/operational-rules" + "/:typeFilter", element: createElement(component), breadCrumb: undefined, permission: "web.cbtc.user.edit"
                    } as IRouteConfig);
                    routes.push({
                        path: v.url + "/operating-notices-and-instructions" + "/:typeFilter", element: createElement(component), breadCrumb: undefined, permission: "web.cbtc.user.edit"
                    } as IRouteConfig);
                } else {
                    routes.push({
                        path: v.url, element: createElement(component), breadCrumb: v.name
                    } as IRouteConfig);
                    routes.push({
                        path: v.url + "/:typeFilter", element: createElement(component), breadCrumb: undefined
                    } as IRouteConfig);
                    routes.push({
                        path: v.url + "/expired" + "/:typeFilter", element: createElement(component), breadCrumb: undefined
                    } as IRouteConfig);
                    routes.push({
                        path: v.url + "/network-rules" + "/:typeFilter", element: createElement(component), breadCrumb: undefined
                    } as IRouteConfig);
                    routes.push({
                        path: v.url + "/operating-notices-and-instructions" + "/:typeFilter", element: createElement(component), breadCrumb: undefined
                    } as IRouteConfig);
                }
            }
        }
    }
    return routes;
};
